/** MSC Super Admin → Residual Analysis: upload an agent's current residual statement and show their MSC deposit. */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, PageHeader, StatusPill, errorText } from '../../components/finance/financeUi';
import { UploadCard } from '../../components/residualAnalysis/UploadCard';
import { AnalysisEditor } from '../../components/residualAnalysis/AnalysisEditor';

const TONE = { EXTRACTING: 'PROCESSING', REVIEW: 'NEEDS_REVIEW', FINAL: 'FINAL' };

export default function ResidualAnalysisPage() {
  const [d, setD] = useState(null);
  const [current, setCurrent] = useState(null);
  const load = useCallback(() => api.request('/admin/residual-analyses').then(setD).catch((e) => toast.error(errorText(e))), []);
  const open = useCallback((id) => api.request(`/admin/residual-analyses/${id}`).then(setCurrent).catch((e) => toast.error(errorText(e))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (current?.status !== 'EXTRACTING') return undefined;
    const t = setTimeout(() => { open(current.analysis_id); load(); }, 4000);
    return () => clearTimeout(t);
  }, [current, open, load]);
  if (!d) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  const changed = (a) => { setCurrent(a); load(); };
  return (
    <div className="space-y-6 pb-24" data-testid="residual-analysis-page">
      <PageHeader eyebrow="Recruiting" title="Residual Analysis" subtitle="Upload an agent's current residual statement. We price every merchant on MSC costs and show their deposit: Revenue − MSC cost = Net residual × share." />
      <UploadCard agents={d.agents} platforms={d.platforms} onCreated={changed} />
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <Card title="Analyses" testid="ra-list">
          <ul className="divide-y">
            {!d.analyses.length && <li className="px-5 py-4 text-sm text-muted-foreground" data-testid="ra-list-empty">No statements uploaded yet.</li>}
            {d.analyses.map((a) => (
              <li key={a.analysis_id}>
                <button type="button" onClick={() => open(a.analysis_id)} className={`w-full px-5 py-3 text-left text-sm transition-colors hover:bg-muted ${current?.analysis_id === a.analysis_id ? 'bg-muted' : ''}`} data-testid={`ra-list-item-${a.analysis_id}`}>
                  <span className="flex items-center justify-between gap-2"><b className="truncate">{a.agent?.name || a.prospect_name}</b><StatusPill value={TONE[a.status] || a.status} /></span>
                  <span className="block truncate text-xs text-muted-foreground">{a.file.filename} · {a.platform} · {new Date(a.created_at).toLocaleDateString()}</span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
        {current ? <AnalysisEditor a={current} agents={d.agents} platforms={d.platforms} onChange={changed} />
          : <div className="grid place-items-center rounded-xl border border-dashed p-10 text-sm text-muted-foreground" data-testid="ra-empty-editor">Pick an analysis or upload a statement.</div>}
      </div>
    </div>
  );
}
